import { createContextApi, invalidatesOne, providesOne, providesList, createCursorPagedEndpoint, registerCrossContextInvalidation } from '@quickbee/store';

// src/index.ts
var SCHEME_TAGS = ["Scheme", "Enrollment", "SipSchedule", "Deposit"];
function listParams(args) {
  const params = {};
  for (const [key, value] of Object.entries(args ?? {})) {
    if (typeof value === "string" && value.trim() === "") continue;
    params[key] = value;
  }
  return params;
}
var schemeEndpoints = (build) => ({
  // ─── /schemes ───────────────────────────────────────────────────────────────
  listSchemes: build.query({
    query: (params) => ({ url: "/schemes", params: listParams(params) }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Scheme", result),
    // Reference data (§10.4): the catalogue changes rarely and is read constantly.
    keepUnusedDataFor: 900
  }),
  /** Returns a plain list, not a page: the saver's catalogue is small by design. */
  listSchemesForCustomer: build.query({
    query: () => ({ url: "/schemes/for-customer" }),
    providesTags: (result) => providesList("Scheme", result),
    keepUnusedDataFor: 300
  }),
  getScheme: build.query({
    query: (id) => ({ url: `/schemes/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Scheme", id),
    keepUnusedDataFor: 900
  }),
  createScheme: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/schemes",
      method: "POST",
      body,
      idempotencyKey: `scheme-create:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "Scheme", id: "LIST" }]
  }),
  /**
   * Send only the fields being changed. ADR-007 locks the financial-terms group
   * once a scheme has any enrollment, and the backend decides which group a
   * request touched by field presence — so echoing an unchanged `monthlyAmount`
   * back with a rename turns a permitted edit into a rejected one.
   */
  updateScheme: build.mutation({
    query: ({ id, ...body }) => ({
      url: `/schemes/${id}`,
      method: "PATCH",
      body,
      skipIdempotencyKey: true
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Scheme", id)
  }),
  activateScheme: build.mutation({
    query: (id) => ({ url: `/schemes/${id}/activate`, method: "POST", skipIdempotencyKey: true }),
    invalidatesTags: (_r, _e, id) => invalidatesOne("Scheme", id)
  }),
  archiveScheme: build.mutation({
    query: (id) => ({ url: `/schemes/${id}/archive`, method: "POST", skipIdempotencyKey: true }),
    invalidatesTags: (_r, _e, id) => invalidatesOne("Scheme", id)
  }),
  // ─── /schemes/enrollments ───────────────────────────────────────────────────
  /**
   * A tenant/any-scoped caller (ops, ledger's account drawer) **must** pass
   * `customerId`; there is no unfiltered mode, and omitting it is a 400. An
   * `own`-scoped caller may omit it — see {@link ListEnrollmentsArgs}.
   */
  listEnrollments: build.query({
    query: (params) => ({ url: "/schemes/enrollments", params: listParams(params) }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Enrollment", result),
    keepUnusedDataFor: 60
  }),
  /** Schemes this customer may still open a plan in — a scheme projection, not an enrollment one. */
  listEligibleSchemes: build.query({
    query: (args) => ({
      url: "/schemes/enrollments/eligible",
      params: listParams(args ?? void 0)
    }),
    providesTags: (result) => providesList("Scheme", result),
    keepUnusedDataFor: 300
  }),
  getEnrollment: build.query({
    query: (id) => ({ url: `/schemes/enrollments/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Enrollment", id),
    keepUnusedDataFor: 60
  }),
  /** Empty for a FLEXIBLE plan — the schedule is the SCHEDULED_SIP half of ADR-011. */
  getSipSchedule: build.query({
    query: (enrollmentId) => ({ url: `/schemes/enrollments/${enrollmentId}/sip-schedule` }),
    providesTags: (_r, _e, id) => [{ type: "SipSchedule", id }],
    keepUnusedDataFor: 60
  }),
  /**
   * Plain list: a FLEXIBLE plan's deposits are bounded by its eleven-month window.
   * Returns `[]` rather than an error for a SCHEDULED_SIP enrollment, so a drawer
   * showing both plan kinds need not branch on mode just to avoid a 4xx.
   */
  getEnrollmentDeposits: build.query({
    query: (enrollmentId) => ({ url: `/schemes/enrollments/${enrollmentId}/deposits` }),
    providesTags: (_r, _e, enrollmentId) => [
      { type: "Deposit", id: enrollmentId }
    ],
    keepUnusedDataFor: 60
  }),
  createEnrollment: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/schemes/enrollments",
      method: "POST",
      body,
      // I9, verbatim from the design: caller-stable, derived from the mutation's
      // identity. A customer who taps "Enroll" twice gets one enrollment.
      idempotencyKey: `enroll:${body.schemeId}:${draftId}`
    }),
    invalidatesTags: [
      { type: "Enrollment", id: "LIST" },
      // The scheme's `enrolled` count and, on the for-customer projection, its
      // `canOpenAnother` both just changed.
      { type: "Scheme", id: "LIST" }
    ]
  }),
  cancelEnrollment: build.mutation({
    query: ({ id, reason }) => ({
      url: `/schemes/enrollments/${id}/cancel`,
      method: "POST",
      body: { reason },
      idempotencyKey: `enrollment-cancel:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Enrollment", id),
      { type: "SipSchedule", id },
      // A cancelled plan stops blocking re-enrollment, so the for-customer row
      // flips back to AVAILABLE.
      { type: "Scheme", id: "LIST" }
    ]
  }),
  /** Ops/RM action once the customer has caught up out-of-band (INV-20); `schemes:enroll:any` only. */
  resumeEnrollment: build.mutation({
    query: ({ id }) => ({
      url: `/schemes/enrollments/${id}/resume`,
      method: "POST",
      skipIdempotencyKey: true
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Enrollment", id)
  }),
  /**
   * ADR-010 — ops write-off of a permanently-MISSED instalment; `schemes:enroll:any`
   * only, and `reason` is mandatory (it is the sole audit note). Returns 204, so
   * the screen refetches the enrollment and its schedule.
   */
  waiveInstallment: build.mutation({
    query: ({ enrollmentId, installmentId, reason }) => ({
      url: `/schemes/enrollments/${enrollmentId}/installments/${installmentId}/waive`,
      method: "POST",
      body: { reason },
      // A waiver moves money. Keyed on the instalment, so a double-click waives once.
      idempotencyKey: `installment-waive:${installmentId}`
    }),
    invalidatesTags: (_r, _e, { enrollmentId }) => [
      ...invalidatesOne("Enrollment", enrollmentId),
      { type: "SipSchedule", id: enrollmentId }
    ]
  })
});

// src/index.ts
var schemeApi = createContextApi({
  context: "scheme",
  reducerPath: "schemeApi",
  tagTypes: SCHEME_TAGS,
  mocks: () => import('./mocks-WTCRA4NT.js').then((module) => module.schemeMocks),
  endpoints: schemeEndpoints
});
var SCHEME_INVALIDATION_RULES = [
  {
    event: "payment.captured",
    invalidate: ({ enrollmentId }) => enrollmentId ? [
      { type: "Enrollment", id: enrollmentId },
      { type: "Enrollment", id: "LIST" },
      { type: "SipSchedule", id: enrollmentId },
      { type: "Deposit", id: enrollmentId },
      { type: "Scheme", id: "LIST" }
    ] : []
  },
  {
    event: "redemption.settled",
    // A settled redemption changes accrued grams on whichever enrollment it drew
    // from; the id is not on the event, so refresh the lists rather than guess.
    invalidate: () => [
      { type: "Enrollment", id: "LIST" },
      { type: "Scheme", id: "LIST" }
    ]
  }
];
function registerSchemeInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: schemeApi,
    rules: SCHEME_INVALIDATION_RULES
  });
}
var {
  useListSchemesQuery,
  useListSchemesForCustomerQuery,
  useGetSchemeQuery,
  useCreateSchemeMutation,
  useUpdateSchemeMutation,
  useActivateSchemeMutation,
  useArchiveSchemeMutation,
  useListEnrollmentsQuery,
  useListEligibleSchemesQuery,
  useGetEnrollmentQuery,
  useGetSipScheduleQuery,
  useGetEnrollmentDepositsQuery,
  useCreateEnrollmentMutation,
  useCancelEnrollmentMutation,
  useResumeEnrollmentMutation,
  useWaiveInstallmentMutation
} = schemeApi;

export { SCHEME_INVALIDATION_RULES, SCHEME_TAGS, registerSchemeInvalidation, schemeApi, useActivateSchemeMutation, useArchiveSchemeMutation, useCancelEnrollmentMutation, useCreateEnrollmentMutation, useCreateSchemeMutation, useGetEnrollmentDepositsQuery, useGetEnrollmentQuery, useGetSchemeQuery, useGetSipScheduleQuery, useListEligibleSchemesQuery, useListEnrollmentsQuery, useListSchemesForCustomerQuery, useListSchemesQuery, useResumeEnrollmentMutation, useUpdateSchemeMutation, useWaiveInstallmentMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map